import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import {
  GET_TEXT_OVERLAY_BY_ID_QUERY_KEY,
  GET_TEXT_OVERLAY_QUERY_KEY,
  TEXT_OVERLAY_API_URL
} from '@/constants/QueryConstants';
import { apiUrl } from '@/helpers';
import {
  CreateTextOverlayPayload,
  ErrorResponse,
  Options,
  TextOverlay,
  TextOverlayPageData,
  UpdateTextOverlayCanvasPayload
} from '@/types';

export const textOverlayEndpoints = {
  addTextOverlay: (payload: CreateTextOverlayPayload) =>
    axios.post(`${apiUrl()}${TEXT_OVERLAY_API_URL}`, payload),
  getTextOverlayById: (id: number) => axios.get(`${apiUrl()}${TEXT_OVERLAY_API_URL}/${id}`),
  getTextOverlayByAssetId: (assetId: number) =>
    axios.get(`${apiUrl()}${TEXT_OVERLAY_API_URL}/asset/${assetId}`),
  getAllTextOverlays: (eventId: number) =>
    axios.get(`${apiUrl()}${TEXT_OVERLAY_API_URL}/event/${eventId}`),
  removeTextOverlays: (id: number) => axios.delete(`${apiUrl()}${TEXT_OVERLAY_API_URL}/${id}`),
  updateTextOverlayCanvas: (payload: UpdateTextOverlayCanvasPayload) =>
    axios.put(`${apiUrl()}${TEXT_OVERLAY_API_URL}/canvas`, payload),
  duplicateTextOverlay: (assetId: number) =>
    axios.post(`${apiUrl()}${TEXT_OVERLAY_API_URL}/${assetId}/duplicate`)
};

export const useCreateTextOverlayMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse<TextOverlay>, ErrorResponse, CreateTextOverlayPayload>(
    textOverlayEndpoints.addTextOverlay,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
        queryClient.invalidateQueries([GET_TEXT_OVERLAY_QUERY_KEY]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateTextOverlayCanvasMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse<TextOverlay>, ErrorResponse, UpdateTextOverlayCanvasPayload>(
    textOverlayEndpoints.updateTextOverlayCanvas,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
        queryClient.invalidateQueries([GET_TEXT_OVERLAY_QUERY_KEY]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useRemoveTextOverlayMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse, ErrorResponse, number>(
    textOverlayEndpoints.removeTextOverlays,
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([GET_TEXT_OVERLAY_QUERY_KEY]);
        options.onSuccess(response);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetAllTextOverlaysQuery = (eventId: number) => {
  return useInfiniteQuery<AxiosResponse<TextOverlayPageData>>([GET_TEXT_OVERLAY_QUERY_KEY], () =>
    textOverlayEndpoints.getAllTextOverlays(eventId)
  );
};

export const useGetTextOverlayByIdQuery = (id: number, options?: Options) => {
  return useQuery<AxiosResponse<TextOverlay>>(
    [GET_TEXT_OVERLAY_BY_ID_QUERY_KEY, { id }],
    () => textOverlayEndpoints.getTextOverlayById(id),
    {
      enabled: !!id,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetTextOverlayByAssetIdMutation = (options?: Options) => {
  return useMutation<AxiosResponse<TextOverlay>, ErrorResponse, number>(
    textOverlayEndpoints.getTextOverlayByAssetId,
    {
      onSuccess: (response) => {
        options?.onSuccess?.(response);
      },
      onError: (error) => {
        options?.onError?.(error as ErrorResponse);
      }
    }
  );
};

export const useDuplicateTextOverlayMutation = (queryClient: QueryClient, options?: Options) => {
  return useMutation<AxiosResponse<TextOverlay>, ErrorResponse, number>(
    textOverlayEndpoints.duplicateTextOverlay,
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([GET_TEXT_OVERLAY_QUERY_KEY]);
        options?.onSuccess?.(response);
      },
      onError: (error) => {
        options?.onError?.(error as ErrorResponse);
      }
    }
  );
};
